/** Analytics service client — events, digests, timeline, safety score. */
import { api, apiBlob } from './client';

export interface TimelineEntry {
  occurred_at: string;
  kind: string;
  label: string;
  detail: string | null;
  severity: 'info' | 'warn' | 'alert';
  payload: Record<string, unknown>;
}

export interface TimelineDay {
  day: string;
  entries: TimelineEntry[];
}

export interface ChildTimeline {
  child_id: string;
  period_days: number;
  days: TimelineDay[];
}

export interface ScoreFactor {
  slug: string;
  name: string;
  weight: number;
  penalty: number;
  value: string;
}

export interface SafetyScore {
  family_id: string;
  period_days: number;
  score: number;
  band: 'great' | 'good' | 'attention' | 'concern';
  factors: ScoreFactor[];
}

export interface PickupActive {
  child_id: string;
  left_school_at: string;
  school_name: string | null;
  from_lat: number | null;
  from_lng: number | null;
  last_seen_at: string;
  approaching: boolean;
  elapsed_minutes: number;
  typical_minutes: number | null;
  running_late: boolean;
}

export type RiskSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface RiskAlert {
  category: string;
  label: string;
  severity: RiskSeverity;
  count: number;
  last_seen_at: string | null;
  advice: string;
}

export interface BlockedCategoryStat {
  category: string;
  label: string;
  count: number;
}

export interface ZoneDwellStat {
  name: string;
  visits: number;
  avg_minutes: number | null;
}

export interface ChildRollup {
  child_id: string;
  blocked: number;
  geofence_events: number;
  safety_alerts: number;
}

export interface MonthlyReport {
  family_id: string;
  year: number;
  month: number;
  month_label: string;
  period_start: string;
  period_end: string;
  blocked_total: number;
  blocked_categories: BlockedCategoryStat[];
  scam_phishing_blocked: number;
  geofence_events: number;
  zones: ZoneDwellStat[];
  safety_alerts: number;
  sos_count: number;
  integrity_signals: number;
  safewalk_deviations: number;
  device_battery_low: number;
  device_offline: number;
  per_child: ChildRollup[];
  screen_time_available: boolean;
  ai_recommendations: string[];
}

export interface MonthlyReportPdfRequest {
  year: number;
  month: number;
  family_name?: string | null;
  children: { id: string; name: string }[];
}

export interface SuggestedAction {
  label: string;
  route: string;
}

export interface AiSummary {
  family_id: string;
  period_days: number;
  summary: string;
  recommendations: string[];
  suggested_action: SuggestedAction | null;
  source: 'ai' | 'rules';
}

export const analyticsApi = {
  timeline: (childId: string, days = 7) =>
    api<ChildTimeline>(`/v1/analytics/children/${childId}/timeline?days=${days}`),
  safetyScore: (days = 7) =>
    api<SafetyScore>(`/v1/analytics/family/safety-score?days=${days}`),
  pickupActive: (hours = 4) =>
    api<PickupActive[]>(`/v1/analytics/family/pickup-active?hours=${hours}`),
  riskAlerts: (days = 7) =>
    api<RiskAlert[]>(`/v1/analytics/family/risk-alerts?days=${days}`),
  monthlyReport: (year?: number, month?: number) => {
    const qs = new URLSearchParams();
    if (year != null) qs.set('year', String(year));
    if (month != null) qs.set('month', String(month));
    const q = qs.toString();
    return api<MonthlyReport>(`/v1/analytics/family/monthly-report${q ? `?${q}` : ''}`);
  },
  monthlyReportPdf: (req: MonthlyReportPdfRequest) =>
    apiBlob('/v1/analytics/family/monthly-report.pdf', { method: 'POST', body: req }),
  aiSummary: () => api<AiSummary>('/v1/analytics/family/ai-summary'),
};
